import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './Timeline.css';
import { fmtDateTime } from '../lib/when';

const ActivityTrail = ({ ticketId, token }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const fetchActivityTrail = useCallback(async () => {
    if (!ticketId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // No explicit header: the staff token is attached by the interceptor.
      // This used to send `Bearer ${token}` from a localStorage key that no
      // longer exists, which arrived as the literal string "Bearer null".
      const response = await axios.get(`${API_BASE}/tickets/${ticketId}/activity`);
      setActivities(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching activity trail:', err);
      setError('Failed to load activity trail');
    } finally {
      setLoading(false);
    }
  }, [ticketId, API_BASE]);

  useEffect(() => {
    fetchActivityTrail();
  }, [fetchActivityTrail]);

  /*
   * The bead, inherited from the duplicate panel that used to sit above this
   * one. There were two timelines on the ticket - "Approval history" and
   * "Activity" - reading two routes that ran the same query through the same
   * mapper, so the lists were identical and only the beads differed: a
   * coloured dot against an emoji.
   *
   * The dot is the one that came across, because it carries information the
   * emoji did not. A status change is coloured by the status it moved TO, so
   * the column of beads reads as the ticket's own path - purple waiting,
   * amber in progress, green approved, grey closed - and a reader can see
   * where a ticket got stuck without reading a word.
   */
  const markerColour = (row) => {
    const byStatus = {
      'Awaiting Approval': '#9B59B6',
      Approved: '#27AE60',
      Open: '#3498DB',
      'In Progress': '#F39C12',
      Resolved: '#27AE60',
      Completed: '#27AE60',
      Closed: '#7F8C8D',
      Rejected: '#E74C3C'
    };
    if (row.status_to && byStatus[row.status_to]) return byStatus[row.status_to];
    const byAction = {
      created: '#A3005C',
      commented: '#3498DB',
      priority_changed: '#F39C12',
      assigned: '#9B59B6',
      status_changed: '#27AE60'
    };
    return byAction[row.action] || '#95A5A6';
  };

  // "02 Oct 09:14", with the year only when it is not this one, and the
  // seconds dropped - nobody has ever needed to know that a ticket was
  // commented on at 41 seconds past. The full stamp is on the hover title, so
  // the precision is still there for anyone who wants it.
  //
  // Also no longer forced to 'en-US', which rendered Maltese dates in American
  // order on a board read in Malta.
  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    const sameYear = date.getFullYear() === new Date().getFullYear();
    return date.toLocaleString('en-GB', { timeZone: 'Europe/Malta',
      day: '2-digit',
      month: 'short',
      ...(sameYear ? {} : { year: 'numeric' }),
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionLabel = (action) => {
    const labels = {
      created: 'Created',
      updated: 'Updated',
      commented: 'Commented',
      attached: 'File Attached',
      approved: 'Approved',
      rejected: 'Rejected',
      assigned: 'Assigned',
      status_changed: 'Status Changed',
      priority_changed: 'Priority Changed',
      resolved: 'Resolved',
      reopened: 'Reopened',
      deleted: 'Deleted',
      default: 'Activity'
    };
    return labels[action] || labels['default'];
  };

  const panel = (inner, count) => (
    <section className="tl-panel activity-trail">
      <div className="tl-head">
        <h3 className="tl-title">Activity</h3>
        {count > 0 && (
          <span className="tl-count">{count} event{count === 1 ? '' : 's'}</span>
        )}
      </div>
      {inner}
    </section>
  );

  if (loading) return panel(<p className="tl-quiet">Loading activity&hellip;</p>, 0);
  if (error) return panel(<p className="tl-error" role="alert">{error}</p>, 0);

  if (!activities || activities.length === 0) {
    return panel(<p className="tl-quiet">No activity recorded yet.</p>, 0);
  }

  return panel(
    <ol className="tl-list">
      {activities.map((activity, index) => {
        return (
          <li key={activity.id || index} className="tl-row">
            {/* The bead is the first column of the row. It used to sit in a
                30px rail of its own beside a card. */}
            <span
              className="tl-dot"
              style={{ backgroundColor: markerColour(activity) }}
              aria-hidden="true"
            />
            <time
              className="tl-when"
              dateTime={activity.created_at || activity.timestamp || undefined}
              title={(activity.created_at || activity.timestamp)
                ? fmtDateTime(activity.created_at || activity.timestamp)
                : undefined}
            >
              {formatDateTime(activity.created_at || activity.timestamp)}
            </time>
            <span className="tl-what">
              <span className="tl-action">{getActionLabel(activity.action)}</span>
              {activity.description && (
                <> &middot; <span className="tl-detail">{activity.description}</span></>
              )}
              {/*
                * There were three more lines here reading activity.details -
                * a field name, and an old \u2192 new pair. The route has never
                * returned a `details` key: there is no such column, and
                * timelineRow does not invent one. They rendered for exactly
                * one caller, a test mock that made the object up, and never
                * once in the application.
                *
                * The before and after are not lost - they are in the
                * description the server composes, "Priority Low \u2192 High",
                * which is where they have actually been coming from all
                * along.
                */}
            </span>
            {/*
              * WHERE IT WAS MADE. Only ever present when the server decided
              * this caller may see it - the person on their own row, or an
              * administrator on anybody's - because routes/tickets.js deletes
              * the coordinates from the row rather than sending them and
              * trusting the browser not to draw them. A field hidden by CSS
              * is a field that leaks the first time somebody opens the
              * network tab.
              *
              * `located` survives for everybody and says only whether a
              * location was recorded at all, which is what makes "this one
              * has none" answerable without showing anyone where anybody was.
              */}
            {activity.location && (
              <a
                className="tl-where"
                href={`https://www.openstreetmap.org/?mlat=${activity.location.lat}`
                  + `&mlon=${activity.location.lng}#map=17/`
                  + `${activity.location.lat}/${activity.location.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                title={`${activity.location.lat.toFixed(5)}, `
                  + `${activity.location.lng.toFixed(5)}`
                  + (activity.location.accuracy_m
                      ? ` (within about ${activity.location.accuracy_m}m)` : '')
                  + ' — opens a map in a new tab'}
              >
                {activity.location.lat.toFixed(4)}, {activity.location.lng.toFixed(4)}
              </a>
            )}
            {!activity.location && activity.location_status
              && activity.location_status !== 'recorded'
              && activity.location_status !== 'off' && (
              <span className="tl-where is-none"
                title="No location was recorded with this change">
                no location
              </span>
            )}

            <span className="tl-who">
              {/* actor_name used to be absent from every row, so this read
                  "System" for the whole timeline. The route now names the
                  member of staff, the contractor, or - when an administrator
                  was working as somebody else - both. */}
              {activity.actor_name || 'System'}
              {activity.actor_is_contractor && <span className="tl-flag"> (contractor)</span>}
              {activity.on_behalf_by && (
                <span className="tl-flag"> &mdash; by {activity.on_behalf_by}</span>
              )}
            </span>
          </li>
        );
      })}
    </ol>,
    activities.length
  );
};

export default ActivityTrail;
